import { EmptyState } from "@/components/molecules/empty-state";
import { Button } from "@/components/ui/button";
import { usePublicTranslation, useTranslation } from "@/features/utils/i18n";
import {
  ArrowClockwiseIcon,
  HouseIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";

/**
 * What a route shows when rendering it threw.
 *
 * Without an `errorElement`, React Router answers with its own developer page
 * ("Hey developer 👋") — in English, unstyled, and addressed to us rather than
 * to the person in front of it. The message itself is only printed as a
 * detail: it is rarely something they can act on, but it is what support will
 * ask them to read out.
 *
 * "Retour à l'accueil" is a full navigation on purpose: the router that threw
 * is the one we would otherwise be asking to recover.
 *
 * `publicChat` is the widget's own version: it runs in an iframe on a tenant's
 * site, in front of their visitor, so it speaks the visitor's words and has no
 * way into the back-office — `/home` there would load our login inside their
 * page.
 */
export function RouteErrorPage({ publicChat = false }: { publicChat?: boolean }) {
  const { t: tApp } = useTranslation("common");
  const { t: tPublic } = usePublicTranslation();
  const error = useRouteError();

  useEffect(() => {
    console.error("Route error:", error);
  }, [error]);

  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : null;

  if (publicChat) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-4">
        <EmptyState
          compact
          icon={WarningCircleIcon}
          title={tPublic("chat.error_title")}
          description={tPublic("chat.error_description")}
          action={
            <Button onClick={() => window.location.reload()}>
              <ArrowClockwiseIcon />
              {tPublic("chat.error_retry")}
            </Button>
          }
        />
      </div>
    );
  }

  const t = tApp;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-lg rounded-xl border bg-background shadow-xs">
        <EmptyState
          compact
          tone="danger"
          icon={WarningCircleIcon}
          title={t("common.routeError.title")}
          description={t("common.routeError.description")}
          className="py-10"
          action={
            <>
              <Button onClick={() => window.location.reload()}>
                <ArrowClockwiseIcon />
                {t("common.routeError.reload")}
              </Button>
              <Button variant="outline" asChild>
                <a href="/home">
                  <HouseIcon />
                  {t("common.routeError.home")}
                </a>
              </Button>
            </>
          }
        />
        {detail && (
          <p className="border-t px-6 py-3 text-center font-mono text-xs text-muted-foreground break-words">
            {detail}
          </p>
        )}
      </div>
    </div>
  );
}
